'use client'

import Button from '@components/ui/ButtonV2/Button'
import FormControl from '@components/ui/inputs/FormControl'
import PhoneNumberInput from '@components/ui/inputs/PhoneNumberInput'
import Select from '@components/ui/inputs/Select'
import TextInput from '@components/ui/inputs/TextInput'
import { useCustomerActivate } from '@features/account/api/customer-activate'
import { useCountries } from '@features/countries/api/get-countries'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCustomErrors } from '@lib/hooks/useCustomErrors'
import { selectCountry } from '@redux/globalSlice'
import clsx from 'clsx'
import Cookies from 'js-cookie'
import { CountryCode, getCountries } from 'libphonenumber-js'
import { signIn } from 'next-auth/react'
import { useEffect } from 'react'
import { Controller, FieldErrors, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { z } from 'zod'
import s from './M55SignupForm.module.scss'

// Enum representing logical account types used in the UI & business logic
export enum AccountTypeEnum {
  Consumer = 'CONSUMER',
  ArchitectDesigner = 'ARCHITECT_DESIGNER',
}

// Central meta map tying enum -> id/label and potential future config
const ACCOUNT_TYPE_META: Record<
  AccountTypeEnum,
  { id: string; label: string }
> = {
  [AccountTypeEnum.Consumer]: {
    id: 'cusgrp_01jj318bzee619jv0rex9d8cnm',
    label: 'Consumer',
  },
  [AccountTypeEnum.ArchitectDesigner]: {
    id: 'cusgrp_01jjps1p5ye76te2w3msakj83b',
    label: 'Architect & Designer',
  },
}

const accountTypeOptions = (
  Object.keys(ACCOUNT_TYPE_META) as AccountTypeEnum[]
).map((key) => ({ value: key, label: ACCOUNT_TYPE_META[key].label }))

// Zod schema stores the enum value (not the id). We map to id on submit.
const baseConfirmAccountSchema = z.object({
  accountType: z.nativeEnum(AccountTypeEnum, { required_error: 'Required' }),
  firstName: z.string().min(1, 'Required'),
  lastName: z.string().min(1, 'Required'),
  password: z
    .string()
    .min(1, 'Required')
    .regex(/[A-Z]/, 'Must contain an uppercase letter')
    .regex(/[a-z]/, 'Must contain a lowercase letter')
    .regex(/\d/, 'Must contain a digit')
    .regex(/[\W_]/, 'Must contain a special character'),
  // Architect & Designer specific (conditionally required)
  businessName: z.string().optional(),
  address1: z.string().optional(),
  address2: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  vatNumber: z.string().optional(),
  phoneNumber: z.string().optional(),
  companyUrl: z.string().optional(),
  instagramProfile: z.string().optional(),
  phoneCountry: z.string().optional(),
})

type BaseConfirmAccount = z.infer<typeof baseConfirmAccountSchema>

const confirmAccountStepFormSchema = baseConfirmAccountSchema.superRefine(
  (data, ctx) => {
    if (data.accountType === AccountTypeEnum.ArchitectDesigner) {
      const requiredArchitectFields: Array<keyof BaseConfirmAccount> = [
        'businessName',
        'address1',
        'postalCode',
        'city',
        'country',
        'vatNumber',
        'phoneNumber',
        'phoneCountry',
      ]
      requiredArchitectFields.forEach((field) => {
        const value = (data as any)[field]
        if (
          !value ||
          (typeof value === 'string' && value.trim().length === 0)
        ) {
          ctx.addIssue({
            path: [field],
            code: z.ZodIssueCode.custom,
            message: 'Required',
          })
        }
      })
    }
  },
)

type ConfirmAccountFormValues = z.infer<typeof confirmAccountStepFormSchema>

interface Props {
  email: string
  token: string
  currentStep: number
  setCurrentStep: (step: number) => void
}

export default function ConfirmAccountStep({
  email,
  token,
  currentStep,
  setCurrentStep,
}: Props) {
  const { addError, loading, customErrors, clearErrors } = useCustomErrors()
  const country = useSelector(selectCountry)
  const form = useForm<ConfirmAccountFormValues>({
    defaultValues: {
      accountType: AccountTypeEnum.Consumer,
      phoneCountry: country.code?.toUpperCase() ?? 'US',
    },
    resolver: zodResolver(confirmAccountStepFormSchema),
  })

  const { data: countriesData } = useCountries({})
  const { data: countriesList } = useCountries()
  const countries = countriesList?.countries || []
  const homeCountry = Cookies.get('homeCountry')
  const countryFieldValue = form.watch('country')
  useEffect(() => {
    if (!countryFieldValue && countries.length) {
      const defaultCountry = homeCountry
        ? countries.find(
            (c) => c.code.toLowerCase() === homeCountry.toLowerCase(),
          )
        : countries[0]
      if (defaultCountry) form.setValue('country', defaultCountry.code)
    }
  }, [countryFieldValue, countries, homeCountry, form])

  const { mutateAsync, isPending } = useCustomerActivate()
  const passwordValue = form.watch('password', '')

  const checkPasswordRequirements = (password: string) => {
    return {
      minLength: password.length >= 6,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /\d/.test(password),
      nonAlphanumeric: /[\W_]/.test(password),
    }
  }
  const passwordRequirements = checkPasswordRequirements(passwordValue)

  // Scroll to first field with an error
  const scrollToFirstError = (
    errors: FieldErrors<ConfirmAccountFormValues>,
  ) => {
    const entries = Object.entries(errors)
    if (!entries.length) return
    const [fieldName] = entries[0]
    // Try direct match first
    let el = document.querySelector<HTMLElement>(`[name="${fieldName}"]`)
    // Fallback: for nested components (e.g., custom selects) look for data-field wrapper
    if (!el) {
      el = document.querySelector<HTMLElement>(`[data-field="${fieldName}"]`)
    }
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        if (typeof (el as any).focus === 'function') {
          ;(el as any).focus({ preventScroll: true })
        }
      })
    }
  }

  const handleSubmit = form.handleSubmit(
    async (data) => {
      const accountTypeId = ACCOUNT_TYPE_META[data.accountType].id

      const resp = await mutateAsync({
        input: {
          email: email,
          token: token,
          firstName: data.firstName,
          lastName: data.lastName,
          password: data.password,
          metadata:
            data.accountType === AccountTypeEnum.ArchitectDesigner
              ? [
                  {
                    key: 'requested_group_id',
                    value: accountTypeId,
                  },
                  {
                    key: 'business_name',
                    value: data.businessName,
                  },
                  {
                    key: 'address1',
                    value: data.address1,
                  },
                  {
                    key: 'address2',
                    value: data.address2 ?? '',
                  },
                  {
                    key: 'postal_code',
                    value: data.postalCode,
                  },
                  {
                    key: 'city',
                    value: data.city,
                  },
                  {
                    key: 'country',
                    value: data.country,
                  },
                  {
                    key: 'phone_number',
                    value: data.phoneNumber,
                  },
                  {
                    key: 'phone_country',
                    value: data.phoneCountry,
                  },
                  {
                    key: 'company_url',
                    value: data.companyUrl ?? '',
                  },
                  {
                    key: 'instagram_profile',
                    value: data.instagramProfile ?? '',
                  },
                  {
                    key: 'vat_number',
                    value: data.vatNumber,
                  },
                ]
              : [],
        },
      })

      if (resp?.customerActivate.errors) {
        resp.customerActivate.errors.forEach((error) => {
          switch (error.code) {
            case 'CustomerInvalidActivationTokenError': {
              addError({
                code: error.code,
                message:
                  'The activation link is invalid or has expired. Please request a new activation link.',
              })
              return
            }
          }
        })
      }

      if (resp.customerActivate.customer) {
        // Push activated_account event to GTM dataLayer on account activation
        try {
          if (typeof window !== 'undefined') {
            ;(window as any).dataLayer = (window as any).dataLayer || []
            ;(window as any).dataLayer.push({
              event: 'activated_account',
              method: 'Activated account',
              email: email,
              account_type:
                data.accountType === AccountTypeEnum.ArchitectDesigner
                  ? 'Architect & Designer'
                  : 'Consumer',
              user_id: resp.customerActivate.customer.id,
            })
          }
        } catch (e) {
          console.warn('Failed to push activated_account event to dataLayer', e)
        }

        //  if (data?.news) {
        //   const newsletterSignupResponse = await signupNewsletter({
        //     email: data?.email,
        //     list: !!data.businessName ? 'PROFESSIONALS' : 'CONSUMERS',
        //     country: country,
        //     language: router.query.language as string,
        //     getCountry: Cookies.get('homeCountry') ?? '',
        //   })

        //   if (!newsletterSignupResponse.ok)
        //     return addError({
        //       code: 'failedNewsletterSignup',
        //       message: 'Failed to sign up for newsletter',
        //     })
        // }

        signIn('credentials', {
          email: email,
          password: data.password,
        }).catch((error) => {
          addError({ code: 'failedToSignIn', message: 'Failed to sign in' })
        })
      }
    },
    (errors) => {
      scrollToFirstError(errors)
    },
  )

  return (
    <form onSubmit={handleSubmit} className={s.form}>
      {customErrors && customErrors.length > 0 && (
        <div className={s.errorContainer}>
          {customErrors.map((error) => (
            <div key={error.code} className={s.error}>
              {error.message}
            </div>
          ))}
        </div>
      )}
      {currentStep === 1 && (
        <>
          <Controller
            control={form.control}
            name="accountType"
            render={({ field, fieldState }) => (
              <FormControl floatingLabel required>
                <FormControl.Label>Account Type</FormControl.Label>
                <Select className="w-full p-2 border" {...field}>
                  {accountTypeOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
                {fieldState.error && (
                  <FormControl.Validation variant="error">
                    {fieldState.error.message}
                  </FormControl.Validation>
                )}
              </FormControl>
            )}
          />
          <div className={s.buttonGroup}>
            <Button
              type="button"
              variant="secondary"
              onClick={async () => {
                const valid = await form.trigger('accountType')
                if (valid) {
                  // Push account_type_selected event to GTM dataLayer when user proceeds from step 1
                  try {
                    if (typeof window !== 'undefined') {
                      ;(window as any).dataLayer =
                        (window as any).dataLayer || []
                      ;(window as any).dataLayer.push({
                        event: 'account_type_selected',
                        method: 'Account type selection',
                        email: email,
                        account_type:
                          form.getValues('accountType') ===
                          AccountTypeEnum.ArchitectDesigner
                            ? 'Architect & Designer'
                            : 'Consumer',
                      })
                    }
                  } catch (e) {
                    console.warn(
                      'Failed to push account_type_selected event to dataLayer',
                      e,
                    )
                  }

                  setCurrentStep(2)
                } else {
                  scrollToFirstError(form.formState.errors)
                }
              }}
              className={s.nextButton}
            >
              Next
            </Button>
          </div>
        </>
      )}
      {currentStep === 2 && (
        <>
          <Controller
            control={form.control}
            name="firstName"
            render={({ field, fieldState }) => (
              <FormControl required floatingLabel>
                <FormControl.Label>First name</FormControl.Label>
                <TextInput className="w-full" {...field} />
                {fieldState.error && (
                  <FormControl.Validation variant="error">
                    {fieldState.error.message}
                  </FormControl.Validation>
                )}
              </FormControl>
            )}
          />
          <Controller
            control={form.control}
            name="lastName"
            render={({ field, fieldState }) => (
              <FormControl required floatingLabel>
                <FormControl.Label>Last name</FormControl.Label>
                <TextInput className="w-full" {...field} />
                {fieldState.error && (
                  <FormControl.Validation variant="error">
                    {fieldState.error.message}
                  </FormControl.Validation>
                )}
              </FormControl>
            )}
          />

          <Controller
            control={form.control}
            name="password"
            render={({ field, fieldState }) => (
              <FormControl floatingLabel required>
                <FormControl.Label>Password</FormControl.Label>
                <TextInput type="password" className="w-full" {...field} />
                {fieldState.error && (
                  <FormControl.Validation variant="error">
                    {fieldState.error.message}
                  </FormControl.Validation>
                )}
              </FormControl>
            )}
          />
          <div className={s.passwordRequirements}>
            <ul>
              <li
                className={clsx(
                  { [s.valid]: passwordRequirements.minLength },
                  {
                    [s.error]:
                      !passwordRequirements.minLength &&
                      form.formState.touchedFields.password,
                  },
                )}
              >
                At least 6 characters long
              </li>
              <li
                className={clsx(
                  {
                    [s.valid]:
                      passwordRequirements.uppercase &&
                      passwordRequirements.lowercase &&
                      passwordRequirements.digit,
                  },
                  {
                    [s.error]:
                      !(
                        passwordRequirements.uppercase &&
                        passwordRequirements.lowercase &&
                        passwordRequirements.digit
                      ) && form.formState.touchedFields.password,
                  },
                )}
              >
                At least one uppercase letter, one lowercase letter, and one
                number
              </li>
              <li
                className={clsx(
                  { [s.valid]: passwordRequirements.nonAlphanumeric },
                  {
                    [s.error]:
                      !passwordRequirements.nonAlphanumeric &&
                      form.formState.touchedFields.password,
                  },
                )}
              >
                At least one special character
              </li>
            </ul>
          </div>

          {form.watch('accountType') === AccountTypeEnum.ArchitectDesigner && (
            <div className={s.architectBlock}>
              <h4 className={s.sectionTitle}>Business details</h4>
              <Controller
                control={form.control}
                name="businessName"
                render={({ field, fieldState }) => (
                  <FormControl floatingLabel required>
                    <FormControl.Label>Business name</FormControl.Label>
                    <TextInput className="w-full" {...field} />
                    {fieldState.error && (
                      <FormControl.Validation variant="error">
                        {fieldState.error.message}
                      </FormControl.Validation>
                    )}
                  </FormControl>
                )}
              />
              <Controller
                control={form.control}
                name="address1"
                render={({ field, fieldState }) => (
                  <FormControl floatingLabel required>
                    <FormControl.Label>Address line 1</FormControl.Label>
                    <TextInput className="w-full" {...field} />
                    {fieldState.error && (
                      <FormControl.Validation variant="error">
                        {fieldState.error.message}
                      </FormControl.Validation>
                    )}
                  </FormControl>
                )}
              />
              <Controller
                control={form.control}
                name="address2"
                render={({ field }) => (
                  <FormControl floatingLabel>
                    <FormControl.Label>
                      Address line 2 (optional)
                    </FormControl.Label>
                    <TextInput className="w-full" {...field} />
                  </FormControl>
                )}
              />
              <div className={s.inlineRow}>
                <Controller
                  control={form.control}
                  name="postalCode"
                  render={({ field, fieldState }) => (
                    <FormControl floatingLabel required>
                      <FormControl.Label>Postal code</FormControl.Label>
                      <TextInput className="w-full" {...field} />
                      {fieldState.error && (
                        <FormControl.Validation variant="error">
                          {fieldState.error.message}
                        </FormControl.Validation>
                      )}
                    </FormControl>
                  )}
                />
                <Controller
                  control={form.control}
                  name="city"
                  render={({ field, fieldState }) => (
                    <FormControl floatingLabel required>
                      <FormControl.Label>City</FormControl.Label>
                      <TextInput className="w-full" {...field} />
                      {fieldState.error && (
                        <FormControl.Validation variant="error">
                          {fieldState.error.message}
                        </FormControl.Validation>
                      )}
                    </FormControl>
                  )}
                />
              </div>
              <Controller
                control={form.control}
                name="country"
                render={({ field, fieldState }) => (
                  <FormControl floatingLabel required>
                    <FormControl.Label>Country</FormControl.Label>
                    <Select className="w-full" {...field}>
                      {countries.map((country) => (
                        <Select.Option
                          key={country.code}
                          value={country.code.toUpperCase()}
                        >
                          {country.name}
                        </Select.Option>
                      ))}
                    </Select>
                    {fieldState.error && (
                      <FormControl.Validation variant="error">
                        {fieldState.error.message}
                      </FormControl.Validation>
                    )}
                  </FormControl>
                )}
              />
              <Controller
                control={form.control}
                name="vatNumber"
                render={({ field, fieldState }) => (
                  <FormControl floatingLabel required>
                    <FormControl.Label>
                      VAT Number (e.g. DK123456789)
                    </FormControl.Label>
                    <TextInput className="w-full" {...field} />
                    {fieldState.error && (
                      <FormControl.Validation variant="error">
                        {fieldState.error.message}
                      </FormControl.Validation>
                    )}
                  </FormControl>
                )}
              />
              <Controller
                control={form.control}
                name="phoneCountry"
                render={({ field: countrySelectField }) => (
                  <Controller
                    control={form.control}
                    name="phoneNumber"
                    render={({ field: phoneField, fieldState }) => (
                      <FormControl className="mt-2" required floatingLabel>
                        <FormControl.Label>Phone number</FormControl.Label>
                        <PhoneNumberInput {...phoneField}>
                          <PhoneNumberInput.CountrySelect
                            required
                            {...countrySelectField}
                          >
                            {countriesData?.countries
                              ?.filter((x) =>
                                getCountries().includes(x.code as CountryCode),
                              )
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((country) => (
                                <PhoneNumberInput.CountrySelect.Option
                                  key={country.code}
                                  value={country.code}
                                >
                                  {country.name}
                                </PhoneNumberInput.CountrySelect.Option>
                              ))}
                          </PhoneNumberInput.CountrySelect>
                        </PhoneNumberInput>
                        {fieldState.error && (
                          <FormControl.Validation variant="error">
                            {fieldState.error.message}
                          </FormControl.Validation>
                        )}
                      </FormControl>
                    )}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="companyUrl"
                render={({ field, fieldState }) => (
                  <FormControl floatingLabel>
                    <FormControl.Label>
                      Company URL (optional)
                    </FormControl.Label>
                    <TextInput className="w-full" {...field} />
                    {fieldState.error && (
                      <FormControl.Validation variant="error">
                        {fieldState.error.message}
                      </FormControl.Validation>
                    )}
                  </FormControl>
                )}
              />
              <Controller
                control={form.control}
                name="instagramProfile"
                render={({ field, fieldState }) => (
                  <FormControl floatingLabel>
                    <FormControl.Label>
                      Instagram profile (optional)
                    </FormControl.Label>
                    <TextInput className="w-full" {...field} />
                    {fieldState.error && (
                      <FormControl.Validation variant="error">
                        {fieldState.error.message}
                      </FormControl.Validation>
                    )}
                  </FormControl>
                )}
              />
            </div>
          )}

          <div className={s.buttonGroup}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setCurrentStep(1)}
              className={s.backButton}
            >
              Back
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isPending}
              className={s.submitButton}
            >
              Create account
            </Button>
          </div>
        </>
      )}
    </form>
  )
}
